import HTTP from '@/utils/http';
import { EventBus } from '@/utils/event-bus';
import i18n from '@/locales';
import { Notification } from 'element-ui';

const defaultState = () => ({
  company: {
    groups: [],
  },
  messages: [],
  mfa: [],
  mergeFields: [],
  checkboxGroups: [],
  loadedMergeFields: false,
  defaultSharingSettings: {},
  mfaEnabled: false,
});

const companyModule = {
  namespaced: true,
  state: defaultState(),
  mutations: {
    RESET_STATE(state) {
      Object.assign(state, defaultState());
    },
    SET(state, { company }) {
      state.company = company;
    },
    SET_MFA_ENABLED(state, { mfaStatus }) {
      state.mfaEnabled = mfaStatus;
    },
    SET_MESSAGES(state, { messages }) {
      state.messages = messages;
    },
    ADD_MESSAGE(state, { message }) {
      state.messages.push(message);
    },
    SET_MERGE_FIELDS(state, { mergeFields }) {
      state.mergeFields = mergeFields;
      state.loadedMergeFields = true;
    },
    SET_MERGE_FIELD(state, { mergeField }) {
      state.mergeFields = state.mergeFields.map((item) => {
        const field = item;
        field.active = false;
        return item;
      });

      state.mergeFields.unshift({
        merge_field: mergeField,
        active: true,
      });
    },
    SET_COMPANY_MFA(state, mfa) {
      state.mfa = mfa;
    },
    SET_CHECKBOX_GROUPS(state, { checkboxGroups }) {
      state.checkboxGroups = checkboxGroups;
    },
    SET_CHECKBOX_GROUP(state, { checkboxGroup }) {
      state.company.groups = state.company.groups.map((item) => {
        const group = item;
        group.active = false;
        return item;
      });

      state.company.groups.unshift({
        group: checkboxGroup,
        active: true,
      });
    },
    SET_DEFAULT_SHARING_SETTINGS(state, settings) {
      state.defaultSharingSettings = settings;
    },
  },
  actions: {
    RESET_STATE({ commit }) {
      commit('RESET_STATE');
    },
    GET({ commit }) {
      return HTTP({
        url: 'company',
        params: {
        },
      }).then((response) => {
        commit('SET', { company: response.data.data });
        commit('SET_COMPANY_MFA', response.data.data);
        return response.data.data;
      });
    },
    UPDATE_MFA_STATUS({ commit }, mfaEnabled) {
      return HTTP({
        method: 'PUT',
        url: 'company/multi-factor-auth',
        data: {
          mfa_enabled: mfaEnabled,
        },
      }).then((response) => {
        commit('SET_COMPANY_MFA', response.data.data);
        return response.data.data;
      });
    },
    async GET_MESSAGES({ commit }) {
      let otherPages = [];

      const { data: { count, limit: limitString, data: firstPage } } = await HTTP({
        url: 'messages',
      });

      const limit = parseInt(limitString, 10);

      if (count > firstPage.length) {
        let offset = 0;
        const requests = [];

        while (offset + limit < count) {
          offset += limit;
          requests.push(HTTP({
            url: 'messages',
            params: {
              offset,
            },
          }));
        }

        const responses = await Promise.all(requests);

        otherPages = responses
          .flatMap(({ data: { data } }) => data);
      }

      const messages = [...firstPage, ...otherPages];


      commit('SET_MESSAGES', { messages });
      return messages;
    },
    ADD_MESSAGE({ commit }, message) {
      return HTTP({
        url: 'messages',
        method: 'post',
        data: message,
        suppressFormErrors: true,
      }).then((response) => {
        commit('ADD_MESSAGE', { message: response.data.data });
        EventBus.$emit('renderGlobalMessage', ({ message: i18n.t('message.success.add', { name: 'message' }), icon: 'check', type: 'success' }));
        return response.data.data;
      });
    },
    DELETE_MESSAGE({ dispatch }, { id, name }) {
      return HTTP({
        method: 'delete',
        url: `messages/${id}`,
      }).then(() => {
        dispatch('GET_MESSAGES');
        EventBus.$emit('renderGlobalMessage', {
          message: i18n.t('createEnvelope.personalMessage.deleted', { name }),
          icon: 'check',
          type: 'success',
        });
      });
    },
    GET_MERGE_FIELDS({ commit }) {
      return HTTP({
        url: 'company/merge-fields/unique',
        params: {
          limit: 99999999999999,
        },
      }).then((response) => {
        commit('SET_MERGE_FIELDS', { mergeFields: response.data.data });
        return response.data.data;
      });
    },
    GET_COMPANY_MFA({ commit }) {
      return HTTP({
        method: 'get',
        url: 'company/multi-factor-auth',
      }).then((response) => {
        commit('SET_COMPANY_MFA', response.data.data);
        return response.data.data;
      });
    },
    ADD_MERGE_FIELD({ commit }, { mergeField }) {
      return HTTP({
        method: 'post',
        url: 'company/merge-fields',
        data: {
          merge_field: mergeField,
        },
      }).then((response) => {
        commit('SET_MERGE_FIELD', { mergeField });
        return response.data.data;
      });
    },
    GET_CHECKBOX_GROUPS({ commit }) {
      return HTTP({
        url: 'company/checkbox-groups',
        params: {
          limit: 99999999999999,
        },
      }).then((response) => {
        commit('SET_CHECKBOX_GROUPS', { checkboxGroups: response.data.data });
        return response.data.data;
      });
    },
    ADD_CHECKBOX_GROUP({ commit }, { checkboxGroup }) {
      return HTTP({
        method: 'post',
        url: 'company/checkbox-groups',
        data: {
          group_name: checkboxGroup,
        },
      }).then((response) => {
        commit('SET_CHECKBOX_GROUPS', { checkboxGroups: response.data.data });

        return response.data.data;
      });
    },
    UPDATE({ commit }, company) {
      return HTTP({
        method: 'put',
        url: 'company',
        params: {
        },
        data: {
          name: company.name,
          email: company.email,
          vat: company.vat,
          account_email: company.account_email,
          telephone: company.telephone,
          address_line_1: company.address_line_1,
          address_line_2: company.address_line_2,
          address_town: company.address_town,
          address_city: company.address_city,
          address_postcode: company.address_postcode,
          billing_overage: company.billing_overage,
          country_code: (company.lock_country || !company.currency_locked) ? company.country_code : undefined, // TODO Update in future so we're not passing everything in
          currency_locked: company.lock_country || undefined,
        },
      }).then((response) => {
        commit('SET', { company: response.data.data });
        EventBus.$emit('renderGlobalMessage', ({ message: i18n.t('message.successfullyUpdated', { name: 'company' }), icon: 'check', type: 'success' }));
      });
    },
    RESEND_VERIFICATION_EMAIL() {
      return HTTP({
        method: 'put',
        url: 'company/resend-verification-email',
      }).then((response) => {
        Notification.success({
          title: i18n.t('dashboard.verification.cta.confirmed'),
          message: i18n.t('dashboard.verification.cta.action'),
          position: 'bottom-left',
        });

        return response.data;
      });
    },
    EXPORT_DATA({}, { type }) { // eslint-disable-line no-empty-pattern
      return HTTP({
        method: 'post',
        url: `exports/${type}`,
      }).then(() => {
        Notification({
          title: i18n.t('companySettings.data.exportStarted'),
          message: i18n.t('companySettings.data.exportStartedMessage'),
          position: 'bottom-left',
          type: 'success',
        });
      });
    },
    GET_DEFAULT_SHARING_SETTINGS({ commit }) {
      return HTTP({
        method: 'GET',
        url: 'company/default-sharing-settings',
      }).then((res) => {
        commit('SET_DEFAULT_SHARING_SETTINGS', res.data.data);
      })
      .catch(() => {
        Notification({
          title: i18n.t('companySettings.developer.defaultSharingSettings.addFailure.title'),
          message: i18n.t('companySettings.developer.defaultSharingSettings.addFailure.message'),
          position: 'bottom-left',
          type: 'error',
        });
      });
    },
    UPDATE_DEFAULT_SHARING_SETTINGS({ commit }, { data }) {
      return HTTP({
        method: 'PUT',
        url: 'company/default-sharing-settings',
        data,
      }).then((res) => {
        commit('SET_DEFAULT_SHARING_SETTINGS', res.data.data);
        Notification({
          title: i18n.t('companySettings.developer.defaultSharingSettings.updateSuccess.title'),
          message: i18n.t('companySettings.developer.defaultSharingSettings.updateSuccess.message'),
          position: 'bottom-left',
          type: 'success',
        });
      })
      .catch(() => {
        Notification({
          title: i18n.t('companySettings.developer.defaultSharingSettings.updateFailure.title'),
          message: i18n.t('companySettings.developer.defaultSharingSettings.updateFailure.message'),
          position: 'bottom-left',
          type: 'error',
        });
      });
    },
  },
  getters: {
    company: state => state.company,
    companyMFA: state => state.mfa,
    groups: state => state.checkboxGroups,
    messages: state => state.messages,
    mergeFields: state => state.mergeFields,
    loadedMergeFields: state => state.loadedMergeFields,
    defaultSharingSettings: state => state.defaultSharingSettings,
    mfaEnabled: state => state.mfa.mfa_enabled,
  },
};

export default companyModule;
